/**
 * ProfessionalControlCardTemplate
 * 
 * STM-style professional control card template implementation.
 * Assembles all sections into the complete control card PDF.
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG } from '../../config'
import type { ControlCardData } from '../../infrastructure'
import { ControlCardTemplateBase, type LogoConfig } from './ControlCardTemplateBase'
import { HeaderSection } from '../sections/HeaderSection'
import { EquipmentSpecificationSection } from '../sections/EquipmentSpecificationSection'
import { InspectionSection } from '../sections/InspectionSection'
import { SignatureSection } from '../sections/SignatureSection'
import { FooterSection } from '../sections/FooterSection'
import { ReportNumberingService } from '../../../../services/report-numbering.service'

/**
 * Render options for the template
 */
export interface ProfessionalTemplateOptions {
  finalize?: boolean
  inspectionId?: string
  attachmentImage?: string // Base64 encoded image for second page
}

import { type PageHeaderConfig } from '../../infrastructure'

/**
 * Professional control card template (STM style)
 */
export class ProfessionalControlCardTemplate extends ControlCardTemplateBase {
  private readonly headerSection: HeaderSection
  private readonly equipmentSection: EquipmentSpecificationSection
  private readonly inspectionSection: InspectionSection
  private readonly signatureSection: SignatureSection
  private readonly footerSection: FooterSection

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    super(config)
    this.headerSection = new HeaderSection(config)
    this.equipmentSection = new EquipmentSpecificationSection(config)
    this.inspectionSection = new InspectionSection(config)
    this.signatureSection = new SignatureSection(config)
    this.footerSection = new FooterSection(config)
  }

  /**
   * Render the complete professional control card
   */
  async renderComplete(
    data: ControlCardData,
    logos?: LogoConfig,
    options?: ProfessionalTemplateOptions
  ): Promise<jsPDF> {
    // Get report number
    const reportNumber = await this.getReportNumber(
      data.inspection,
      options?.inspectionId,
      options?.finalize
    )

    // Update inspection data with report number
    const inspectionWithNumber = {
      ...data.inspection,
      reportNumber,
    }

    const dataWithReportNumber = {
      ...data,
      inspection: inspectionWithNumber,
    }

    // Use base template method
    return this.render(dataWithReportNumber, logos, options)
  }

  /**
   * Render header section
   */
  protected async renderHeader(
    data: ControlCardData,
    logos?: LogoConfig
  ): Promise<number> {
    const doc = this.pdfRenderer.getDocument()
    const reportNumber = data.inspection.reportNumber || 'N/A'

    const finalY = await this.headerSection.render(doc, data, reportNumber, logos)
    return finalY
  }

  /**
   * Render body section (equipment + inspection)
   */
  protected async renderBody(
    data: ControlCardData,
    startY: number,
    logos?: LogoConfig
  ): Promise<number> {
    const doc = this.pdfRenderer.getDocument()
    let currentY = startY

    // Prepare page header configuration for pagination
    const pageHeader: PageHeaderConfig = {
      reportNumber: data.inspection.reportNumber || 'N/A',
      ownerName: data.mainEquipment.ownerName || 'UNKNOWN OWNER',
      serviceOrder: data.mainEquipment.serviceOrder
    }

    // Prepare continuation header data (includes logos for page 2+)
    const continuationHeaderData = {
      reportNumber: pageHeader.reportNumber,
      ownerName: pageHeader.ownerName,
      serviceOrder: pageHeader.serviceOrder,
      logos
    }

    // Create continuation header callback for page breaks
    const continuationHeaderCallback = this.headerSection.createContinuationHeaderCallback(continuationHeaderData)

    // 1. Render all equipment specifications (main + sub-equipment)
    currentY = await this.equipmentSection.renderAll(
      doc,
      data.mainEquipment,
      data.subEquipment,
      currentY,
      pageHeader,
      continuationHeaderCallback
    )

    // 2. Check if there's minimal space left - only force page break if almost no room
    // Let autoTable handle natural pagination for inspection tables to maximize page usage
    const pageHeight = 297 // A4 height in mm
    const marginBottom = this.config.layout.margin.bottom
    // Footer is just a single text line at the very bottom, doesn't need extra reservation
    const minUsableSpace = 15 // Only force break if less than this (bare minimum for any content)
    
    const availableSpace = pageHeight - marginBottom - currentY
    
    // Only force page break if there's almost no usable space left
    // Otherwise let tables flow naturally and handle their own pagination
    if (availableSpace < minUsableSpace) {
      const borderStartX = this.config.layout.tableStartX
      const borderWidth = this.config.layout.tableWidth
      
      // Draw bottom border to close the current content
      doc.setLineWidth(this.config.borders.standard)
      doc.setDrawColor(...this.config.colors.border)
      doc.line(borderStartX, currentY, borderStartX + borderWidth, currentY)

      doc.addPage()
      
      // Add full header with logos on new page
      currentY = await this.headerSection.renderContinuationHeader(doc, continuationHeaderData)
    }

    // 3. Calculate space and expand remarks to anchor signature at bottom
    const signatureHeight = 35 // Approximate height of signature section
    const footerSpace = 10 // Space for footer text
    const usableBottom = pageHeight - marginBottom - footerSpace
    const borderStartX = this.config.layout.tableStartX
    const borderWidth = this.config.layout.tableWidth
    
    // Fixed heights for sections after remarks (approximate, increased for larger font sizes)
    const regulatoryHeight = 12
    const testControlHeight = 20
    const inspectionDetailsHeight = 20
    const statusCodesHeight = 10
    const fixedSectionsHeight = regulatoryHeight + testControlHeight + inspectionDetailsHeight + statusCodesHeight + signatureHeight
    
    // Calculate available space for remarks to fill
    const availableForRemarks = usableBottom - currentY - fixedSectionsHeight
    // Use at least the default min height, or expand to fill available space
    const remarksMinHeight = Math.max(this.config.spacing.remarksMinHeight, availableForRemarks)
    
    // Render inspection section with calculated remarks height
    currentY = this.inspectionSection.render(
      doc,
      data.inspection,
      data.mainEquipment,
      currentY,
      remarksMinHeight
    )

    // 4. Check if signature fits on current page (should always fit now, but safety check)
    if (currentY + signatureHeight > usableBottom) {
      // Draw bottom border to close current content
      doc.setLineWidth(this.config.borders.standard)
      doc.setDrawColor(...this.config.colors.border)
      doc.line(borderStartX, currentY, borderStartX + borderWidth, currentY)
      
      // Add new page with full header
      doc.addPage()
      currentY = await this.headerSection.renderContinuationHeader(doc, continuationHeaderData)
    }
    
    // 5. Render signature section (should now be at/near bottom)
    currentY = await this.signatureSection.render(doc, data.inspection, currentY)

    return currentY
  }

  /**
   * Render footer section
   */
  protected async renderFooter(_data: ControlCardData): Promise<void> {
    const doc = this.pdfRenderer.getDocument()
    this.footerSection.render(doc)
  }

  /**
   * Get or generate report number for this inspection
   */
  private async getReportNumber(
    inspection: ControlCardData['inspection'],
    inspectionId?: string,
    finalize?: boolean
  ): Promise<string> {
    // If report number is already provided, use it
    if (inspection.reportNumber) {
      return inspection.reportNumber
    }

    // If inspection ID is provided, try to get existing report number
    if (inspectionId) {
      const existingNumber = await ReportNumberingService.getInspectionReportNumber(
        inspectionId
      )
      if (existingNumber) {
        return existingNumber
      }
    }

    // If finalizing, get the next actual number (increments sequence)
    if (finalize) {
      try {
        return await ReportNumberingService.getNextReportNumber('STMI')
      } catch (error) {
        console.error('Error getting final report number:', error)
        return 'STMI-ERROR'
      }
    }

    // For preview purposes, peek at the next number without incrementing
    try {
      return await ReportNumberingService.peekNextReportNumber('STMI')
    } catch (error) {
      console.error('Error getting preview report number:', error)
      return 'STMI-PREVIEW'
    }
  }
}
